// ProvenanceFooter — the per-card provenance footer.
//
// REPLACES the inline `[[tel:]]` chips (do not render those). One row per CITED
// SOURCE (ME-CARDS-R1 A3): signal_key (store-mapped, GUID-guarded) + read_source
// + window + count + source-grain label (rollup_cagg => "as of last completed
// bucket").
//
// ME-CARDS-R1 A5: presented as the prototype's two-line mono split — a
// signal-KEY line listing every cited key, then the meta line(s) carrying store,
// window, n, grain and the card's `as_of`. Presentation only; the entries come
// from the engine unchanged and nothing is rendered that the payload lacks.
import React from "react";

import type { FooterEntry } from "./honestyEngine";
import { formatIsoDateTimeDDMMYYYY } from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";
import { getUiLocale } from "../../../utils/relativeTime";

export interface ProvenanceFooterProps {
  entries: FooterEntry[];
  /** the card's generation clock (`freshness.as_of`) — rendered on the meta line
   *  when present. Payload data; never synthesised. */
  asOf?: string | null;
}

const windowText = (e: FooterEntry): string => {
  if (e.window) return e.window;
  if (e.windowStart && e.windowEnd) return `${e.windowStart} → ${e.windowEnd}`;
  return "";
};

export const ProvenanceFooter: React.FC<ProvenanceFooterProps> = ({ entries, asOf }) => {
  const { translate } = useTranslation("machineEvidence");
  if (!entries || entries.length === 0) return null;

  // The grain label is CLIENT chrome keyed on the closed `source_grain` enum; an
  // unknown grain falls back to the engine's verbatim label (never a false claim).
  const grainText = (e: FooterEntry): string =>
    e.sourceGrain
      ? (translate(`grain.${e.sourceGrain}`, { defaultValue: e.grainLabel }) as string)
      : e.grainLabel;

  const keys = entries.map((e) => e.signalKey).filter((k) => !!k);
  const asOfText = formatIsoDateTimeDDMMYYYY(asOf);
  const multi = entries.length > 1;

  return (
    <div
      className="msc-provenance"
      style={{
        borderTop: `1px solid ${dt.rowBorder}`,
        paddingTop: 8,
        marginTop: 8,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        fontFamily: dt.fontMono,
        fontSize: 11,
        color: dt.textMuted,
      }}
    >
      {/* Line 1 — every cited signal key (GUID-guarded upstream). */}
      {keys.length > 0 ? (
        <div className="msc-provenance-keys" style={{ color: dt.textSecondary, fontWeight: 600 }}>
          {keys.join(" · ")}
        </div>
      ) : null}

      {/* Line 2+ — the meta split: store · window · n · grain, one per source.
          The key is repeated ONLY when several sources share the footer. */}
      {entries.map((e) => (
        <div
          // ME-CARDS-R1 A3: the row IS the cited source (one row per source_ref
          // id), so the source id is the collision-free key.
          key={e.sourceId}
          className="msc-provenance-row"
          style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "baseline" }}
        >
          {multi && e.signalKey ? <span>{e.signalKey}</span> : null}
          {e.readSource ? <span>{e.readSource}</span> : null}
          {windowText(e) ? <span>· {windowText(e)}</span> : null}
          {typeof e.count === "number" ? <span>· n={e.count.toLocaleString(getUiLocale())}</span> : null}
          {e.grainLabel ? <span>· {grainText(e)}</span> : null}
        </div>
      ))}

      {asOfText ? (
        <div className="msc-provenance-asof">
          {translate("provenance.asOf", { at: asOfText, defaultValue: "card generated {{at}}" }) as string}
        </div>
      ) : null}
    </div>
  );
};

export default ProvenanceFooter;
